import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { passtrackColors } from "../theme";

interface MapPickerProps {
  lat: string;
  lng: string;
  onChange: (lat: string, lng: string) => void;
  height?: number;
}

// Thin Leaflet wrapper. Init/sync logic ported from the design reference's
// initMap/syncMap/attachMap (Setup de Propiedad.dc.html script, ~line 843-877):
// a draggable pin, click-to-move, kept in sync with externally-edited lat/lng inputs.
export default function MapPicker({
  lat,
  lng,
  onChange,
  height = 300,
}: MapPickerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!containerRef.current) return;

    const initialLat = parseFloat(lat) || 19.42604;
    const initialLng = parseFloat(lng) || -99.17153;

    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView([initialLat, initialLng], 16);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const icon = L.divIcon({
      className: "",
      iconSize: [26, 26],
      iconAnchor: [13, 26],
      html: `<div style="width:20px;height:20px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${passtrackColors.blue};border:3px solid #fff;box-shadow:0 4px 10px rgba(10,42,107,.35)"></div>`,
    });

    const marker = L.marker([initialLat, initialLng], {
      icon,
      draggable: true,
    }).addTo(map);

    marker.on("dragend", () => {
      const pos = marker.getLatLng();
      onChangeRef.current(pos.lat.toFixed(5), pos.lng.toFixed(5));
    });

    map.on("click", (e: L.LeafletMouseEvent) => {
      marker.setLatLng(e.latlng);
      onChangeRef.current(e.latlng.lat.toFixed(5), e.latlng.lng.toFixed(5));
    });

    const CenterOnPinControl = L.Control.extend({
      onAdd: () => {
        const container = L.DomUtil.create(
          "div",
          "leaflet-bar leaflet-control",
        );
        const button = L.DomUtil.create("a", "", container) as HTMLAnchorElement;
        button.href = "#";
        button.title = "Centrar en el pin";
        button.setAttribute("role", "button");
        button.setAttribute("aria-label", "Centrar en el pin");
        button.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"
            style="display:block;margin:auto;">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"
              fill="${passtrackColors.blue}"/>
          </svg>
        `;
        button.style.display = "flex";
        button.style.alignItems = "center";
        button.style.justifyContent = "center";
        button.style.width = "30px";
        button.style.height = "30px";
        button.style.lineHeight = "30px";
        button.style.cursor = "pointer";

        L.DomEvent.disableClickPropagation(button);
        L.DomEvent.on(button, "click", (event) => {
          L.DomEvent.preventDefault(event);
          const pos = marker.getLatLng();
          map.setView(pos, Math.max(map.getZoom(), 16), { animate: true });
        });

        return container;
      },
    });

    new CenterOnPinControl({ position: "topright" }).addTo(map);

    mapRef.current = map;
    markerRef.current = marker;

    setTimeout(() => map.invalidateSize(), 120);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    const nextLat = parseFloat(lat);
    const nextLng = parseFloat(lng);
    if (Number.isNaN(nextLat) || Number.isNaN(nextLng)) return;
    const current = marker.getLatLng();
    if (
      Math.abs(current.lat - nextLat) > 1e-6 ||
      Math.abs(current.lng - nextLng) > 1e-6
    ) {
      marker.setLatLng([nextLat, nextLng]);
      map.setView([nextLat, nextLng], map.getZoom(), { animate: true });
    }
  }, [lat, lng]);

  return (
    <Box
      ref={containerRef}
      sx={{
        height,
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: passtrackColors.borderSubtle,
        background: passtrackColors.mist,
        cursor: "crosshair",
        position: "relative",
        zIndex: 0,
        isolation: "isolate",
      }}
    />
  );
}
